import { observer } from "mobx-react-lite"
import {
  CruiseLineId,
  CruiseLineLoyaltyMember,
  ITripBookingPassenger,
  SaveProfileInput,
  TripID,
  TripRoomID,
  UserID,
} from "@cruisen/contract"
import * as yup from "yup"
import { _t } from "@cruisen/i18n"
import {
  getUserTitle,
  addressRegex,
  nameRegex,
  formatDate,
  convertMonthDayYearToDateString,
} from "@cruisen/react-shared"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { saveProfile } from "../../../services/firebase-functions/profile"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { makeAutoObservable } from "mobx"
import { useEffect, useMemo } from "react"
import { SkeletonTheme } from "react-loading-skeleton"
import { colors } from "@theme/colors"
import styles from "./styles.module.scss"
import CloseModalButton from "../../buttons/CloseModalButton"
import { DataGrid } from "../../organisms/BookingModal/components/DataGrid"
import PersonOutline from "~icons/eva/person-outline"
import HomeOutline from "~icons/eva/home-outline"
import ShieldOutline from "~icons/eva/shield-outline"
import StarOutline from "~icons/eva/star-outline"
import Checkmark from "~icons/eva/checkmark-outline"
import { getUTCDate } from "../../organisms/BookingModal/utils"
import Config from "../../../config"
import { useStores } from "../../../models/helpers/useStores"

class PassengersDetailsModalStore {
  isSubmittingForm: boolean
  isUsingPrimaryPassengerResidence: boolean
  constructor() {
    this.isSubmittingForm = false
    this.isUsingPrimaryPassengerResidence = false

    makeAutoObservable(this)
  }

  setIsSubmittingForm(value: boolean) {
    this.isSubmittingForm = value
  }

  setIsUsingPrimaryPassengerResidence(value: boolean) {
    this.isUsingPrimaryPassengerResidence = value
  }
}

type PassengerFormValues = {
  title: string
  firstName: string
  lastName: string
  dob: string
  nationality: string
  address: string
  county: string
  postcode: string
  city: string
  country: string
  passportNumber: string
  passportExpireDate: string
  passportIssueDate: string
  passportIssuingCountry: string
  loyaltyNumber: string
  email: string
}

const passengerSchema = yup.object().shape({
  title: yup.string().required("-"),
  firstName: yup.string().required("-").matches(nameRegex, _t("validation.useOnlyEnglishLetters")),
  lastName: yup.string().required("-").matches(nameRegex, _t("validation.useOnlyEnglishLetters")),
  dob: yup.string().required("-"),
  nationality: yup.string().required("-"),
  address: yup.string().required("-").matches(addressRegex, _t("validation.useOnlyEnglishLetters")),
  county: yup
    .string()
    .when("country", ([country], schema) => (country !== "US" ? schema : schema.required("-"))),
  postcode: yup.string().required("-"),
  city: yup.string().required("-").matches(addressRegex, _t("validation.useOnlyEnglishLetters")),
  country: yup.string().required("-"),
  passportNumber: yup
    .string()
    .test(
      "passportNumber",
      _t("validation.passportNumberMinLength"),
      (value) => !value || value.length >= 5,
    ),
  passportExpireDate: yup.string(),
  passportIssueDate: yup.string(),
  passportIssuingCountry: yup.string(),
  loyaltyNumber: yup.string(),
  email: yup.string().optional(),
})

export const PassengerDetailsModal = observer(function PassengerDetailsModal({
  defaultData,
  tripId,
  cruiseLineId,
  lineName,
  loggedInUserId,
  primaryProfile,
  onSubmit,
  onClose,
  defaultLoyaltyMember,
  guestRoomId,
}: {
  defaultData: ITripBookingPassenger
  tripId: TripID
  cruiseLineId: CruiseLineId
  lineName: string
  loggedInUserId: UserID
  primaryProfile: ITripBookingPassenger | null
  onSubmit: (passengerData: ITripBookingPassenger) => void
  onClose: () => void
  defaultLoyaltyMember?: CruiseLineLoyaltyMember | null
  guestRoomId: TripRoomID
}) {
  const store = useMemo(() => new PassengersDetailsModalStore(), [])
  const queryClient = useQueryClient()
  const rootStore = useStores()

  const { mutateAsync: mutateSaveProfile } = useMutation({
    mutationFn: saveProfile,
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ["allProfiles", loggedInUserId, tripId] })
        .catch((error) => console.error("profile query invalidation failure", error))
    },
  })

  const defaultDob = defaultData?.dob
    ? formatDate(getUTCDate(defaultData.dob).toISOString(), "MM/dd/yyyy")
    : ""
  const defaultPassportIssueDate = defaultData?.passportIssueDate
    ? formatDate(getUTCDate(defaultData.passportIssueDate).toISOString(), "MM/dd/yyyy")
    : ""
  const defaultPassportExpireDate = defaultData?.passportExpireDate
    ? formatDate(getUTCDate(defaultData.passportExpireDate).toISOString(), "MM/dd/yyyy")
    : ""

  const {
    control,
    watch,
    formState: { isValid },
    setValue,
    handleSubmit,
    getValues,
    reset,
    trigger,
  } = useForm({
    defaultValues: {
      title: defaultData?.title || "",
      firstName: defaultData?.firstName || "",
      lastName: defaultData?.lastName || "",
      dob: defaultDob || "",
      nationality: defaultData?.nationality || "",
      address: defaultData?.address || "",
      county: defaultData?.county || "",
      postcode: defaultData?.postcode || "",
      city: defaultData?.city || "",
      country: defaultData?.country || "US",
      passportNumber: defaultData?.passportNumber || "",
      passportExpireDate: defaultPassportExpireDate || "",
      passportIssueDate: defaultPassportIssueDate || "",
      passportIssuingCountry: defaultData?.passportIssuingCountry || "",
      loyaltyNumber: defaultLoyaltyMember?.loyaltyCode || "",
      email: rootStore.loggedInUser?.email || "",
    },
    resolver: yupResolver(passengerSchema),
    mode: "all",
  })

  const setPrimaryPassengerResidence = (isUsingPrimaryPassengerResidence: boolean) => {
    if (isUsingPrimaryPassengerResidence) {
      setValue("address", primaryProfile?.address || "")
      setValue("city", primaryProfile?.city || "")
      setValue("county", primaryProfile?.country !== "US" ? "" : primaryProfile?.county || "")
      setValue("postcode", primaryProfile?.postcode || "")
      setValue("country", primaryProfile?.country || "US")
    } else {
      setValue("address", "")
      setValue("city", "")
      setValue("county", "")
      setValue("postcode", "")
      setValue("country", "US")
    }
    trigger()
  }

  const submitForm = async (values: PassengerFormValues) => {
    store.setIsSubmittingForm(true)
    const payload: SaveProfileInput = {
      profile: {
        id: defaultData.profileId,
        title: values.title,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        dob: convertMonthDayYearToDateString(values.dob),
        ageGroup: defaultData.ageGroup,
        nationality: values.nationality,
        address: values.address.trim(),
        country: values.country,
        county: values.county,
        postcode: values.postcode,
        city: values.city.trim(),
        passportNumber: values.passportNumber,
        passportExpireDate: convertMonthDayYearToDateString(values.passportExpireDate),
        passportIssueDate: convertMonthDayYearToDateString(values.passportIssueDate),
        passportIssuingCountry: values.passportIssuingCountry,
        passportTokenId: defaultData.passportTokenId,
        ownerUserId: defaultData.profileOwnerId || defaultData?.userId,
        isPrimaryProfile: Boolean(defaultData?.userId),
      },
      tripId,
      tripRoomGuestData: {
        roomId: guestRoomId,
        guestId: defaultData.tripRoomGuestId,
      },
      isAtCheckoutScreen: true,
    }

    if (values.email && values.email !== rootStore.loggedInUser?.email) {
      payload.profile.email = values.email
    }

    if (values.loyaltyNumber && values.loyaltyNumber !== defaultLoyaltyMember?.loyaltyCode) {
      payload.cruiseLineLoyaltyMember = {
        cruiseLineId,
        loyaltyCode: values.loyaltyNumber,
      }
    }

    await mutateSaveProfile(payload)

    onSubmit({
      ...values,
      profileId: defaultData.profileId,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      address: values.address.trim(),
      city: values.city.trim(),
      userId: defaultData.userId,
      profileOwnerId: defaultData.profileOwnerId,
      tripRoomGuestId: defaultData.tripRoomGuestId,
      ageGroup: defaultData.ageGroup,
      passportNumber: values.passportNumber,
      passportIssueDate: values.passportIssueDate,
      passportExpireDate: values.passportExpireDate,
      passportIssuingCountry: values.passportIssuingCountry,
      passportTokenId: defaultData.passportTokenId,
      loyaltyNumber: values.loyaltyNumber,
    })
    store.setIsSubmittingForm(false)
    onClose()
  }

  const [titleValue, countryValue, emailValue] = watch(["title", "country", "email"])
  const userTitle = getUserTitle()

  useEffect(() => {
    if (countryValue !== "US") {
      setValue("county", "")
    }
  }, [countryValue])

  const isEmailIncluded = loggedInUserId === defaultData.userId
  const isEmailFilled = isEmailIncluded ? emailValue !== "" : true

  return (
    <>
      <SkeletonTheme
        baseColor={colors.backgroundAdditional}
        highlightColor={colors.backgroundQuaternary}
      >
        <div className={styles.container}>
          <div className={styles.header}>
            <div>
              <h2>Passenger details</h2>
              <p>
                {defaultData.firstName} {defaultData.lastName}
              </p>
            </div>
            <CloseModalButton onClick={onClose} />
          </div>
          <div className={styles.scrollContainer} data-testid="room-booking-modal">
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>General</h3>
              <DataGrid
                icon={<PersonOutline className={styles.sectionIcon} />}
                title="Personal info"
                defaultFormValues={
                  isEmailIncluded
                    ? { email: defaultData?.email || rootStore.loggedInUser?.email || "" }
                    : {}
                }
                data={[
                  {
                    label: "Title",
                    value: userTitle.find((user) => user.value === titleValue)?.label || "",
                    formName: "title",
                  },
                  {
                    label: "First",
                    value: defaultData.firstName || "",
                    formName: "firstName",
                  },
                  {
                    label: "Last",
                    value: defaultData.lastName || "",
                    formName: "lastName",
                  },
                  isEmailIncluded
                    ? {
                        label: "Email",
                        value: defaultData?.email || rootStore.loggedInUser?.email || "",
                        formName: "email",
                      }
                    : null,
                  {
                    label: "Date of birth",
                    value: defaultDob || "",
                    formName: "dob",
                  },
                  {
                    label: "Nationality",
                    value: defaultData.nationality || "",
                    formName: "nationality",
                  },
                ].filter((val) => val !== null)}
                forceEdit={true}
                parentControl={control}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                parentSetValue={(name, value) => setValue(name as any, value)}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                parentGetValues={(name) => getValues(name as any)}
              />
              <DataGrid
                icon={<ShieldOutline className={styles.sectionIcon} />}
                title="Passport details"
                data={[
                  {
                    label: "Passport number",
                    value: defaultData.passportNumber || "",
                    formName: "passportNumber",
                  },
                  {
                    label: "Issuing country",
                    value: defaultData.passportIssuingCountry || "",
                    formName: "passportIssuingCountry",
                  },
                  {
                    label: "Issue date",
                    value: defaultPassportIssueDate || "",
                    formName: "passportIssueDate",
                  },
                  {
                    label: "Expire date",
                    value: defaultPassportExpireDate || "",
                    formName: "passportExpireDate",
                  },
                ]}
                forceEdit={true}
                parentControl={control}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                parentSetValue={(name, value) => setValue(name as any, value)}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                parentGetValues={(name) => getValues(name as any)}
              />
              <DataGrid
                icon={<StarOutline className={styles.sectionIcon} />}
                title="Loyalty numbers"
                data={[
                  {
                    label: lineName,
                    value: defaultLoyaltyMember?.loyaltyCode || "",
                    formName: "loyaltyNumber",
                  },
                ]}
                forceEdit={true}
                parentControl={control}
              />
              {primaryProfile && defaultData.userId !== loggedInUserId && (
                <button
                  className={styles.useResidenceButton}
                  onClick={() => {
                    const incomingValue = !store.isUsingPrimaryPassengerResidence
                    store.setIsUsingPrimaryPassengerResidence(incomingValue)
                    setPrimaryPassengerResidence(incomingValue)
                  }}
                >
                  <p>Use my residence</p>
                  {store.isUsingPrimaryPassengerResidence ? (
                    <div className={styles.checkedBox}>
                      <Checkmark />
                    </div>
                  ) : (
                    <div className={styles.uncheckedBox} />
                  )}
                </button>
              )}
              <DataGrid
                icon={<HomeOutline className={styles.sectionIcon} />}
                title="Residence"
                data={[
                  {
                    label: "Address",
                    value: defaultData.address || "",
                    formName: "address",
                  },
                  {
                    label: "City",
                    value: defaultData.city || "",
                    formName: "city",
                  },
                  countryValue === "US"
                    ? {
                        label: "State",
                        value: defaultData.county || "",
                        formName: "county",
                      }
                    : null,
                  {
                    label: "Postcode",
                    value: defaultData.postcode || "",
                    formName: "postcode",
                  },
                  {
                    label: "Country",
                    value: defaultData.country || "",
                    formName: "country",
                  },
                ].filter((val) => val !== null)}
                forceEdit={true}
                parentControl={control}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                parentSetValue={(name, value) => setValue(name as any, value)}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                parentGetValues={(name) => getValues(name as any)}
              />
            </div>
          </div>
          <div className={styles.footer}>
            <button
              className={styles.cancelButton}
              disabled={store.isSubmittingForm}
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
            {Config.environment === "development" && (
              <button
                className={styles.saveButton}
                onClick={() => {
                  reset({
                    title: "mr",
                    firstName: "John",
                    lastName: "Doe",
                    dob: "1990-01-01",
                    nationality: "US",
                    address: "123 Main St",
                    county: "CA",
                    postcode: "12345",
                    city: "Los Angeles",
                    country: "US",
                    passportNumber: "1234567890",
                    passportIssuingCountry: "US",
                    passportIssueDate: "2020-01-01",
                    passportExpireDate: "2025-01-01",
                    loyaltyNumber: "1234567890",
                  })
                }}
              >
                Fill data
              </button>
            )}
            <button
              className={styles.saveButton}
              disabled={store.isSubmittingForm || !isEmailFilled || !isValid}
              type="button"
              data-testid="save-passenger-details-button"
              onClick={handleSubmit(submitForm)}
            >
              {store.isSubmittingForm ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </SkeletonTheme>
    </>
  )
})
